import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  Building2,
  Store,
  Package,
  Hammer,
  BookOpen,
  MessageCircle,
} from "lucide-react";

type UserRole = "tenant" | "owner";

const HelpCentre: React.FC = () => {
  const [role, setRole] = useState<UserRole>("tenant");
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_searchQuery, setSearchQuery] = useState("");

  const categories = [
    {
      id: "getting-started",
      title: "Getting Started",
      icon: <BookOpen className="w-6 h-6" />,
      link: "/help/getting-started",
    },
    {
      id: "shop-fronts",
      title: "Retail & Shopfronts",
      icon: <Store className="w-6 h-6" />,
      link: "/help/retail-shopfronts",
    },
    {
      id: "workshops",
      title: "Industrial & Workshops",
      icon: <Hammer className="w-6 h-6" />,
      link: "/help/industrial-workshops",
    },
    {
      id: "storage",
      title: "Storage & Logistics",
      icon: <Package className="w-6 h-6" />,
      link: "/help/storage-logistics",
    },
    {
      id: "compliance",
      title: "Legal & Compliance",
      icon: <Building2 className="w-6 h-6" />,
      link: "/help/legal-compliance",
    },
    {
      id: "support",
      title: "Contact Support",
      icon: <MessageCircle className="w-6 h-6" />,
      link: "/help/contact-support",
    },
  ];

  const faqs = {
    tenant: [
      {
        q: "How do I verify a space's zoning for my business?",
        a: "Every listing includes a 'Permitted Use' section. We recommend checking with local council for specific business licenses.",
      },
      {
        q: "Are utilities included in commercial stays?",
        a: "This varies by host. Look for the 'Utilities Included' badge on the listing details.",
      },
    ],
    owner: [
      {
        q: "How do I set hourly vs monthly rates?",
        a: "In your Dashboard, you can toggle between 'Flex-stay' (hourly/daily) or 'Long-term' lease settings.",
      },
      {
        q: "What insurance do I need to host a workshop?",
        a: "We require all hosts to have Public Liability insurance; however, we also offer a Host Protection Guarantee.",
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      {/* Hero Section */}
      <header className="bg-blue-900 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-bold mb-6">
            How can we help your business grow?
          </h1>
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-3.5 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search for permits, insurance, space types..."
              className="w-full p-4 pl-12 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto py-12 px-4">
        {/* Role Toggle */}
        <div className="flex justify-center mb-12">
          <div className="bg-gray-200 p-1 rounded-xl flex">
            <button
              onClick={() => setRole("tenant")}
              className={`px-8 py-2 rounded-lg font-medium transition ${
                role === "tenant"
                  ? "bg-white shadow-sm text-blue-700"
                  : "text-gray-600"
              }`}
            >
              I am a Tenant
            </button>
            <button
              onClick={() => setRole("owner")}
              className={`px-8 py-2 rounded-lg font-medium transition ${
                role === "owner"
                  ? "bg-white shadow-sm text-blue-700"
                  : "text-gray-600"
              }`}
            >
              I am an Owner
            </button>
          </div>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={cat.link}
              className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition cursor-pointer group"
            >
              <div className="text-blue-600 mb-4 group-hover:scale-110 transition-transform">
                {cat.icon}
              </div>
              <h3 className="text-xl font-semibold mb-2">{cat.title}</h3>
              <p className="text-gray-500 text-sm">
                Find information regarding {cat.title.toLowerCase()} for your
                business needs.
              </p>
            </Link>
          ))}
        </div>

        {/* Featured FAQs */}
        <section className="bg-white rounded-2xl p-8 shadow-sm">
          <h2 className="text-2xl font-bold mb-8">
            Popular Questions for {role === "tenant" ? "Tenants" : "Owners"}
          </h2>
          <div className="space-y-6">
            {faqs[role].map((faq, index) => (
              <div
                key={index}
                className="border-b border-gray-100 pb-6 last:border-0"
              >
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  {faq.q}
                </h4>
                <p className="text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default HelpCentre;
